// Scene 2: pick a package (search, grid, "show more").

import React, { useMemo, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2 } from "../context/TransportV2Context";
import { usePackageList } from "../hooks/usePackageList";

const INITIAL_VISIBLE = 6;

// Two-letter monogram built from the initials of the package name, rendered
// in signal orange (see .pkg-mark).
function pkgMark(name) {
  if (!name) return "??";
  const cleaned = String(name).replace(/[^a-zA-Z]/g, " ").trim().toUpperCase();
  if (!cleaned) return "??";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).slice(0, 2);
  return parts[0].slice(0, 2);
}

function formatDate(v) {
  if (!v) return null;
  try {
    // SAP OData date format: /Date(1720000000000)/
    if (typeof v === "string" && v.startsWith("/Date(")) {
      const m = /\/Date\((\d+)\)\//.exec(v);
      if (m) return new Date(+m[1]).toLocaleDateString("de-DE");
    }
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString("de-DE");
  } catch {}
  return null;
}

export default function Scene2Package() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const { packages, loading, error, reload } = usePackageList(state.sourceInstanceId);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    if (!q.trim()) return packages;
    const s = q.toLowerCase();
    return packages.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.id || "").toLowerCase().includes(s));
  }, [packages, q]);

  const visible = q || showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hidden = filtered.length - visible.length;

  const pick = (p) => {
    dispatch({
      type: "PICK_PACKAGE",
      payload: { id: p.id, name: p.name, version: p.version, mod: p.mod },
    });
  };

  return (
    <section className="scene active scene-enter" id="s2">
      <div className="sh">
        <h2>{t("s2.h2")}</h2>
        <p>{t("s2.p")}</p>
      </div>

      <div className="searchrow">
        <div className="search-wrap">
          <svg className="ic ic-16"><use href="#ic-search" /></svg>
          <input
            className="inp"
            type="search"
            placeholder={t("s2.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="tiny muted">
          {state.pkgId ? t("s2.sel", { name: state.pkgName || state.pkgId }) : ""}
        </span>
      </div>

      {loading && <div className="muted" style={{ padding: 20 }}>{t("common.loading")}</div>}
      {error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.packages")}: {error}</span>
          <button className="btn btn-sec btn-sm" onClick={reload}>{t("err.retry")}</button>
        </div>
      )}

      <div className={"pkg-grid" + (state.pkgId ? " has-selection" : "")}>
        {visible.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className={"pkg-card" + (state.pkgId === p.id ? " selected" : "") + " stagger-in"}
            style={{ "--i": i }}
            onClick={() => pick(p)}
          >
            <span className="row1">
              <span className="pkg-mark">{pkgMark(p.name)}</span>
              <span className="pkg-name">{p.name}</span>
              <span className="card-check">
                <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path className="check-path" d="M5 12.5 L10 17.5 L19 6.5" />
                </svg>
              </span>
            </span>
            <span className="pkg-id">{p.id}{p.version ? ` · ${t("s2.ver", { v: p.version })}` : ""}</span>
            <span className="pkg-meta">
              {p.shortText && <span>{p.shortText}</span>}
              {formatDate(p.mod) && <span>{t("s2.mod", { d: formatDate(p.mod) })}</span>}
            </span>
          </button>
        ))}
        {!loading && !error && filtered.length === 0 && (
          <span className="muted">{t("s2.none")}</span>
        )}
      </div>

      {hidden > 0 && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-sec btn-sm" onClick={() => setShowAll(true)}>
            {t("s2.more", { n: hidden })}
          </button>
        </div>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 1 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 3 })} disabled={!state.pkgId}>
          {t("s2.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>
    </section>
  );
}
